import React, { useState } from 'react';
import type { Client, Program } from '../../types';
import { Button } from '../shared';
import { DayView } from './DayView';
import styles from './ProgramView.module.css';

interface ProgramViewProps {
  client: Client;
  programs: Program[];
  isLoading: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
  onBack: () => void;
}

export const ProgramView: React.FC<ProgramViewProps> = ({
  client,
  programs,
  isLoading,
  isGenerating,
  onGenerate,
  onBack,
}) => {
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(programs[0] || null);
  const [activeDay, setActiveDay] = useState(0);

  const currentProgram = selectedProgram || programs[0];

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading programs...</p>
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onBack}>
            ← Back
          </button>
          <h2 className={styles.title}>{client.name}&apos;s Programs</h2>
        </div>

        <div className={styles.empty}>
          <div className={styles.emptyIcon}>🤖</div>
          <h3 className={styles.emptyTitle}>No programs yet</h3>
          <p className={styles.emptyText}>
            Generate a personalized AI training program for {client.name}.
          </p>
          <Button
            onClick={onGenerate}
            isLoading={isGenerating}
            size="large"
          >
            Generate Program
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onBack}>
          ← Back
        </button>
        <div className={styles.headerInfo}>
          <h2 className={styles.title}>{client.name}&apos;s Program</h2>
          <span className={styles.programMeta}>
            {currentProgram?.weeks} weeks • {currentProgram?.days.length} days/week
          </span>
        </div>
      </div>

      {currentProgram?.aiNote && (
        <div className={styles.aiNote}>
          <span className={styles.aiNoteLabel}>🤖 AI Note:</span>
          <p className={styles.aiNoteText}>{currentProgram.aiNote}</p>
        </div>
      )}

      {programs.length > 1 && (
        <div className={styles.programSelector}>
          <label>Program:</label>
          <select
            value={currentProgram?.id}
            onChange={(e) => {
              const prog = programs.find(p => p.id === e.target.value);
              if (prog) {
                setSelectedProgram(prog);
                setActiveDay(0);
              }
            }}
          >
            {programs.map((prog, idx) => (
              <option key={prog.id} value={prog.id}>
                Program {programs.length - idx} - {new Date(prog.createdAt).toLocaleDateString()}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={styles.dayTabs}>
        {currentProgram?.days.map((day, idx) => (
          <button
            key={idx}
            className={`${styles.dayTab} ${activeDay === idx ? styles.active : ''}`}
            onClick={() => setActiveDay(idx)}
          >
            {day.day}
          </button>
        ))}
      </div>

      {currentProgram && (
        <DayView day={currentProgram.days[activeDay]} />
      )}

      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={onGenerate}
          isLoading={isGenerating}
          fullWidth
        >
          Generate New Program
        </Button>
      </div>

      {/* PDF Export placeholder */}
      <button className={styles.pdfButton} disabled>
        📄 Export PDF (Coming Soon)
      </button>
    </div>
  );
};
